/**
 * Duels 1v1 — vue liste + création (réutilisabilité — Étape 8, lot 2).
 * Vitrine du nouveau langage design : DomainCTA (bandeau dégradé duel) + StatusPill.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Coins, Loader2, X, MessageCircle, Send } from "lucide-react";
import { duelsApi, bizCoinsApi, authApi, accountApi, type DuelData } from "../lib/api";
import { DomainCTA } from "./ui/domain-cta";
import { StatusPill } from "./ui/status-pill";
import { EmptyState } from "./ui/empty-state";
import { Button } from "./ui/button";

function initialsAva(name: string, i: number) {
  const grads = ["linear-gradient(135deg,#C0423F,#F6C847)", "linear-gradient(135deg,#5A4BBE,#A6D3F9)", "linear-gradient(135deg,#7CBF94,#A6D3F9)"];
  return <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white text-xs font-bold" style={{ backgroundImage: grads[i % grads.length] }}>{name.slice(0, 1).toUpperCase()}</span>;
}

export function DuelsView({ onPlayDuel }: { onPlayDuel?: (duel: DuelData) => void }) {
  const { t, i18n } = useTranslation();
  const fr = i18n.language === "fr";
  const [duels, setDuels] = useState<DuelData[]>([]);
  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState(0);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [openChat, setOpenChat] = useState<string | null>(null);

  const load = () => Promise.all([duelsApi.list(), bizCoinsApi.getWallet().catch(() => null)])
    .then(([d, w]) => { setDuels(d); if (w) setBalance(w.balance); })
    .catch(() => setDuels([]))
    .finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const act = async (id: string, kind: "accept" | "decline") => {
    setBusy(id);
    const res = kind === "accept" ? await duelsApi.accept(id) : await duelsApi.decline(id);
    if (!res.ok && res.error) alert(res.error);
    await load();
    setBusy(null);
  };

  const pill = (d: DuelData) => {
    if (d.status === "pending") return d.role === "opponent"
      ? <StatusPill tone="wait">{fr ? "À accepter" : "To accept"}</StatusPill>
      : <StatusPill tone="info">{fr ? "En attente" : "Pending"}</StatusPill>;
    if ((d.status === "accepted" || d.status === "played") && !d.iPlayed)
      return <StatusPill gradientVar="var(--grad-duel)">{fr ? "À toi de jouer" : "Your turn"}</StatusPill>;
    if ((d.status === "accepted" || d.status === "played") && d.iPlayed)
      return <StatusPill tone="info">{fr ? "Adversaire à jouer" : "Awaiting opponent"}</StatusPill>;
    if (d.status === "settled") {
      if (d.winnerId === null) return <StatusPill tone="info">{fr ? "Égalité" : "Tie"}</StatusPill>;
      return d.iWon
        ? <StatusPill tone="success">{fr ? "Gagné 🎉" : "Won 🎉"}</StatusPill>
        : <StatusPill tone="danger">{fr ? "Perdu" : "Lost"}</StatusPill>;
    }
    if (d.status === "declined") return <StatusPill tone="info">{fr ? "Décliné" : "Declined"}</StatusPill>;
    return <StatusPill tone="info">{fr ? "Expiré" : "Expired"}</StatusPill>;
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin" style={{ color: "#5A4BBE" }} /></div>;

  return (
    <div className="space-y-4" data-tour="duels-view">
      <div className="flex flex-wrap items-center gap-3 rounded-2xl border bg-white p-4 shadow-[var(--elev-1)]" style={{ borderTopWidth: 3, borderTopColor: "#C0423F" }}>
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-xl" style={{ background: "#FBE7E6" }}>⚔️</span>
        <div className="min-w-0 flex-1">
          <h3 className="font-bold" style={{ color: "#384872", fontFamily: "Sora, sans-serif" }}>{fr ? "Défier un collègue" : "Challenge a colleague"}</h3>
          <p className="text-xs text-muted-foreground">{fr ? "Choisis l'adversaire, le cas, la mise — le gagnant rafle les BizCoins." : "Pick opponent, case, stake — winner takes the BizCoins."}</p>
        </div>
        <Button onClick={() => setCreating(true)} className="shrink-0 text-white" style={{ background: "#C0423F" }}>{fr ? "+ Nouveau duel" : "+ New duel"}</Button>
      </div>

      {duels.length === 0 ? (
        <EmptyState
          icon="⚔️"
          iconGradient="var(--grad-duel)"
          title={fr ? "Aucun duel pour l'instant" : "No duels yet"}
          description={fr ? "Lance un défi à un collègue pour te mesurer à lui." : "Challenge a colleague to compete."}
        />
      ) : (
        <div className="rounded-2xl border bg-white overflow-hidden">
          {duels.map((d, i) => (
            <div key={d.id} className="border-b last:border-b-0">
            <div className="flex items-center gap-3 px-4 py-3">
              {initialsAva(d.opponentName, i)}
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm truncate" style={{ color: "#384872" }}>
                  {d.role === "opponent" && d.status === "pending"
                    ? (fr ? `${d.opponentName} te défie` : `${d.opponentName} challenges you`)
                    : (fr ? `Duel vs ${d.opponentName}` : `Duel vs ${d.opponentName}`)}
                </div>
                <div className="text-xs text-muted-foreground truncate">
                  {d.caseName ? `${d.caseName} · ` : ""}{fr ? "difficulté" : "difficulty"} {d.difficulty}
                </div>
              </div>
              {d.stakeCoins > 0 && (
                <span className="flex items-center gap-1 text-sm font-bold shrink-0" style={{ color: d.iWon ? "#1E7A3C" : "#926B00" }}>
                  <Coins className="h-3.5 w-3.5" />{d.iWon ? `+${d.stakeCoins * 2}` : d.stakeCoins}
                </span>
              )}
              {pill(d)}
              {d.status === "pending" && d.role === "opponent" && (
                <div className="flex gap-1 shrink-0">
                  <Button size="sm" variant="duel" disabled={busy === d.id} onClick={() => act(d.id, "accept")}>{fr ? "Accepter" : "Accept"}</Button>
                  <Button size="sm" variant="ghost" disabled={busy === d.id} onClick={() => act(d.id, "decline")}>{fr ? "Décliner" : "Decline"}</Button>
                </div>
              )}
              {(d.status === "accepted" || d.status === "played") && !d.iPlayed && (
                <Button size="sm" variant="duel" className="shrink-0" onClick={() => onPlayDuel?.(d)}>{fr ? "Jouer" : "Play"}</Button>
              )}
              <button
                onClick={() => setOpenChat(openChat === d.id ? null : d.id)}
                className="shrink-0 p-1.5 rounded-lg text-gray-400 hover:text-[#5A4BBE] hover:bg-[#5A4BBE0f]"
                title={fr ? "Chambrer" : "Chat"}
              >
                <MessageCircle className="h-4 w-4" />
              </button>
            </div>
            {openChat === d.id && <DuelChat duelId={d.id} />}
            </div>
          ))}
        </div>
      )}

      {creating && <CreateDuelDialog balance={balance} onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} />}
    </div>
  );
}

function CreateDuelDialog({ balance, onClose, onCreated }: { balance: number; onClose: () => void; onCreated: () => void }) {
  const { i18n } = useTranslation();
  const fr = i18n.language === "fr";
  const [opponents, setOpponents] = useState<Array<{ id: string; firstName: string | null; lastName: string | null }>>([]);
  const [agents, setAgents] = useState<Array<{ id: string; name: string }>>([]);
  const [allTargets, setAllTargets] = useState<any[]>([]);
  const [opponentId, setOpponentId] = useState("");
  const [simulatorId, setSimulatorId] = useState("");
  const [targetId, setTargetId] = useState("");
  const [difficulty, setDifficulty] = useState("medium");
  const [metric, setMetric] = useState<"objective" | "progression">("objective");
  const [stake, setStake] = useState("50");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    duelsApi.opponents().then(setOpponents).catch(() => setOpponents([]));
    authApi.getAgents().then((a: any[]) => setAgents((a || []).filter((x) => x.simulatorType !== "coach").map((x) => ({ id: x.id, name: x.name })))).catch(() => setAgents([]));
    accountApi.getTargets().then((tg: any[]) => setAllTargets(tg || [])).catch(() => setAllTargets([]));
  }, []);

  // Cas jouables : ceux du simulateur choisi qui ont au moins une persona.
  const cases = allTargets.filter((tg) => tg.simulatorId === simulatorId && (tg.personae || []).length > 0);
  const caseLabel = (tg: any) => tg.displayName || [tg.industry, tg.companySize].filter(Boolean).join(" · ") || (fr ? "Cas" : "Case");

  const submit = async () => {
    setError(null);
    if (!opponentId || !simulatorId) { setError(fr ? "Adversaire et simulateur requis." : "Opponent and simulator required."); return; }
    const st = Number(stake) || 0;
    if (st > balance) { setError(fr ? "Mise supérieure à ton solde." : "Stake exceeds your balance."); return; }
    setSaving(true);
    const res = await duelsApi.create({ opponentId, simulatorId, targetId: targetId || null, difficulty, stakeCoins: st, metric });
    setSaving(false);
    if ("error" in res) { setError(res.error); return; }
    onCreated();
  };

  const label = "block text-xs font-semibold mb-1";
  const inp = "w-full rounded-lg border px-3 py-2 text-sm";
  const inpStyle = { borderColor: "#E6E4EE", color: "#384872" };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl" style={{ boxShadow: "var(--elev-3)" }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-lg" style={{ color: "#384872", fontFamily: "Sora, sans-serif" }}>⚔️ {fr ? "Nouveau duel" : "New duel"}</h3>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-700"><X className="h-5 w-5" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className={label} style={{ color: "#5A6076" }}>{fr ? "Adversaire" : "Opponent"}</label>
            <select className={inp} style={inpStyle} value={opponentId} onChange={(e) => setOpponentId(e.target.value)}>
              <option value="">{fr ? "— Choisir —" : "— Select —"}</option>
              {opponents.map((o) => <option key={o.id} value={o.id}>{o.firstName} {o.lastName}</option>)}
            </select>
          </div>
          <div>
            <label className={label} style={{ color: "#5A6076" }}>{fr ? "Simulateur" : "Simulator"}</label>
            <select className={inp} style={inpStyle} value={simulatorId} onChange={(e) => { setSimulatorId(e.target.value); setTargetId(""); }}>
              <option value="">{fr ? "— Choisir —" : "— Select —"}</option>
              {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>
          {simulatorId && (
            <div>
              <label className={label} style={{ color: "#5A6076" }}>{fr ? "Cas (imposé aux deux joueurs)" : "Case (imposed on both players)"}</label>
              <select className={inp} style={inpStyle} value={targetId} onChange={(e) => setTargetId(e.target.value)}>
                <option value="">{cases.length ? (fr ? "— Choisir un cas —" : "— Select a case —") : (fr ? "Aucun cas — scénario généré" : "No case — generated scenario")}</option>
                {cases.map((tg) => <option key={tg.id} value={tg.id}>{caseLabel(tg)}</option>)}
              </select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={label} style={{ color: "#5A6076" }}>{fr ? "Difficulté" : "Difficulty"}</label>
              <select className={inp} style={inpStyle} value={difficulty} onChange={(e) => setDifficulty(e.target.value)}>
                <option value="easy">{fr ? "Facile" : "Easy"}</option>
                <option value="medium">{fr ? "Moyenne" : "Medium"}</option>
                <option value="hard">{fr ? "Difficile" : "Hard"}</option>
              </select>
            </div>
            <div>
              <label className={label} style={{ color: "#5A6076" }}>{fr ? "Mise (BizCoins)" : "Stake (BizCoins)"}</label>
              <input type="number" min={0} className={inp} style={inpStyle} value={stake} onChange={(e) => setStake(e.target.value)} />
            </div>
          </div>
          <div>
            <label className={label} style={{ color: "#5A6076" }}>{fr ? "Gagnant déterminé sur" : "Winner decided by"}</label>
            <select className={inp} style={inpStyle} value={metric} onChange={(e) => setMetric(e.target.value as any)}>
              <option value="objective">{fr ? "Objectif atteint" : "Objective met"}</option>
              <option value="progression">{fr ? "Progression" : "Progression"}</option>
            </select>
          </div>
          <p className="text-xs text-muted-foreground">🪙 {fr ? "Ton solde" : "Your balance"} : {balance} BizCoins</p>
          {error && <p className="text-sm font-medium" style={{ color: "#C0423F" }}>{error}</p>}
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>{fr ? "Annuler" : "Cancel"}</Button>
          <Button variant="duel" onClick={submit} disabled={saving}>{saving ? "…" : (fr ? "Lancer le défi" : "Send challenge")}</Button>
        </div>
      </div>
    </div>
  );
}

function DuelChat({ duelId }: { duelId: string }) {
  const { i18n } = useTranslation();
  const fr = i18n.language === "fr";
  const [messages, setMessages] = useState<Array<{ id: string; body: string; mine: boolean }>>([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);

  const load = () => duelsApi.chatList(duelId).then(setMessages).catch(() => setMessages([]));
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [duelId]);

  const send = async () => {
    const body = text.trim();
    if (!body) return;
    setSending(true);
    try { await duelsApi.chatSend(duelId, body); setText(""); await load(); }
    finally { setSending(false); }
  };

  return (
    <div className="px-4 pb-3" style={{ background: "#5A4BBE08" }}>
      <div className="max-h-48 overflow-y-auto py-2 space-y-1.5">
        {messages.length === 0 ? (
          <p className="text-xs text-muted-foreground py-2">{fr ? "Lance la causette 😏" : "Start the banter 😏"}</p>
        ) : messages.map((m) => (
          <div key={m.id} className={`flex ${m.mine ? "justify-end" : "justify-start"}`}>
            <span
              className="inline-block rounded-2xl px-3 py-1.5 text-sm max-w-[80%]"
              style={m.mine
                ? { backgroundImage: "var(--grad-signature)", color: "#fff" }
                : { background: "#fff", color: "#384872", border: "1px solid var(--border)" }}
            >
              {m.body}
            </span>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 pt-1">
        <input
          className="flex-1 rounded-lg border px-3 py-1.5 text-sm"
          style={{ borderColor: "var(--border)", color: "#384872" }}
          placeholder={fr ? "Un petit mot…" : "Say something…"}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") send(); }}
          maxLength={1000}
        />
        <button onClick={send} disabled={sending || !text.trim()} className="p-2 rounded-lg text-white disabled:opacity-50" style={{ backgroundImage: "var(--grad-signature)" }}>
          <Send className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
